import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  X,
  ArrowRight,
  Zap,
  Building2,
  Star,
  ChevronDown,
  MessageSquare
} from 'lucide-react';
import { LandingLayout } from '../components/LandingLayout';
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  ScaleIn
} from '../components/AnimatedSection';
import { useLanguage } from '../i18n';

export const PricingPage = () => {
  const [isYearly, setIsYearly] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { t, dir } = useLanguage();

  const plans = [
    {
      name: t.pricing.starter.name,
      description: t.pricing.freemiumDesc,
      isFree: true,
      priceNote: t.pricing.freeWeek,
      features: [
        { name: t.pricing.voiceDoc, included: true },
        { name: t.pricing.aiSoap, included: true },
        { name: `100 ${t.pricing.consultations}`, included: true },
        { name: t.pricing.basicTemplates, included: true },
        { name: t.pricing.emailSupport, included: true },
        { name: t.pricing.ehrIntegration, included: false },
        { name: t.pricing.imageAnalysis, included: false },
        { name: t.pricing.customTemplates, included: false },
        { name: t.pricing.analytics, included: false },
      ],
      cta: t.pricing.startFree,
      highlighted: false,
      icon: Zap
    },
    {
      name: t.pricing.professional.name,
      description: t.pricing.professional.description,
      monthlyPrice: 200,
      yearlyPrice: 199,
      perUser: true,
      features: [
        { name: t.pricing.voiceDoc, included: true },
        { name: t.pricing.aiSoap, included: true },
        { name: `${t.pricing.unlimited} ${t.pricing.consultations}`, included: true },
        { name: t.pricing.allTemplates, included: true },
        { name: t.pricing.prioritySupport, included: true },
        { name: t.pricing.ehrIntegration, included: true },
        { name: t.pricing.imageAnalysis, included: true },
        { name: t.pricing.customTemplates, included: true },
        { name: t.pricing.analytics, included: false },
      ],
      cta: t.pricing.startTrial,
      highlighted: true,
      badge: t.pricing.professional.badge,
      icon: Star
    },
    {
      name: t.pricing.enterprise.name,
      description: t.pricing.enterpriseDesc,
      monthlyPrice: 1000,
      yearlyPrice: 999,
      priceNote: t.pricing.enterpriseNote,
      features: [
        { name: t.pricing.voiceDoc, included: true },
        { name: t.pricing.aiSoap, included: true },
        { name: t.pricing.unlimitedUsers, included: true },
        { name: t.pricing.analytics, included: true },
        { name: t.pricing.customAi, included: true },
        { name: t.pricing.dedicatedManager, included: true },
        { name: t.pricing.sso, included: true },
        { name: t.pricing.customIntegrations, included: true },
        { name: t.pricing.phoneSupport, included: true },
      ],
      cta: t.pricing.contactSales,
      highlighted: false,
      icon: Building2
    },
  ];

  const faqs = [
    {
      question: 'How does the free trial work?',
      answer: 'Start with a 2-day free trial on any plan. No credit card required. You get full access to all features in your chosen plan. Cancel anytime before the trial ends and you won\'t be charged.'
    },
    {
      question: 'Can I switch plans later?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. When upgrading, you\'ll get immediate access to new features. When downgrading, changes take effect at the next billing cycle.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. MedKit.ai is fully HIPAA and GDPR compliant with end-to-end encryption, SOC 2 Type II certification, and regular third-party security audits. Your patient data is always protected.'
    },
    {
      question: 'Which EHR systems do you integrate with?',
      answer: 'We integrate with all major EHR systems including Epic, Cerner, Meditech, Allscripts, athenahealth, and NextGen. Contact us for custom integrations.'
    },
    {
      question: 'Do you offer discounts for multiple users?',
      answer: 'Yes, we offer volume discounts for teams and organizations. Contact our sales team to discuss pricing for your team size.'
    },
    {
      question: 'What kind of support do you provide?',
      answer: 'All plans include email support. Professional plans get priority support with faster response times. Enterprise customers get dedicated account managers and 24/7 phone support.'
    },
  ];

  const comparison = [
    { feature: t.pricing.voiceDoc, starter: true, professional: true, enterprise: true },
    { feature: t.pricing.aiSoap, starter: true, professional: true, enterprise: true },
    { feature: t.pricing.consultations, starter: '100', professional: t.pricing.unlimited, enterprise: t.pricing.unlimited },
    { feature: t.pricing.ehrIntegration, starter: false, professional: true, enterprise: true },
    { feature: t.pricing.imageAnalysis, starter: false, professional: true, enterprise: true },
    { feature: t.pricing.customTemplates, starter: false, professional: true, enterprise: true },
    { feature: t.pricing.analytics, starter: false, professional: false, enterprise: true },
    { feature: t.pricing.customAi, starter: false, professional: false, enterprise: true },
    { feature: t.pricing.sso, starter: false, professional: false, enterprise: true },
    { feature: t.pricing.dedicatedManager, starter: false, professional: false, enterprise: true },
  ];

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden" dir={dir}>
        <div className="absolute inset-0 bg-gradient-to-b from-forest/5 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-bark-700 leading-tight mb-6">
              {t.pricing.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                {t.pricing.titleHighlight}
              </span>
            </h1>
            <p className="text-xl text-bark-700/60 mb-10">
              {t.pricing.description}
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 p-1.5 bg-sand-300/30 rounded-xl">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  !isYearly
                    ? 'bg-sand-50 text-bark-700 shadow-sm'
                    : 'text-bark-700/60 hover:text-bark-700'
                }`}
              >
                {t.pricing.monthly}
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isYearly
                    ? 'bg-sand-50 text-bark-700 shadow-sm'
                    : 'text-bark-700/60 hover:text-bark-700'
                }`}
              >
                {t.pricing.yearly}
                <span className="ms-2 px-2 py-0.5 bg-forest/10 text-forest text-xs rounded-full">
                  {t.pricing.save}
                </span>
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 lg:py-20" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <StaggerContainer className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className={`relative bg-sand-50 rounded-3xl p-8 h-full flex flex-col ${
                    plan.highlighted
                      ? 'ring-2 ring-forest shadow-xl shadow-forest/10'
                      : 'border border-sand-300 shadow-lg'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2">
                      <span className="inline-block px-4 py-1.5 bg-forest text-white text-sm font-medium rounded-full">
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center mb-4">
                      <plan.icon className="w-6 h-6 text-forest" />
                    </div>
                    <h3 className="text-2xl font-bold text-bark-700">{plan.name}</h3>
                    <p className="text-bark-700/60 mt-1">{plan.description}</p>
                  </div>

                  <div className="mb-8">
                    {plan.isFree ? (
                      <div>
                        <span className="text-4xl font-bold text-forest">{t.pricing.free}</span>
                        <p className="text-sm text-bark-700/60 mt-1">{plan.priceNote}</p>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-bark-700">
                            €{isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                          </span>
                          <span className="text-bark-700/60">
                            {plan.perUser ? t.pricing.perUserMonth : t.pricing.perMonth}
                          </span>
                        </div>
                        {plan.priceNote && !isYearly && (
                          <p className="text-sm text-bark-700/60 mt-1">{plan.priceNote}</p>
                        )}
                        {isYearly && (
                          <p className="text-sm text-bark-700/60 mt-1">
                            {t.pricing.billedAnnually} (€{(plan.yearlyPrice * 12).toFixed(2)}/year)
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-forest flex-shrink-0" />
                        ) : (
                          <X className="w-5 h-5 text-sand-400 flex-shrink-0" />
                        )}
                        <span className={feature.included ? 'text-bark-700' : 'text-bark-700/40'}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className={`w-full py-3.5 rounded-xl font-semibold text-center transition-all block ${
                      plan.highlighted
                        ? 'bg-bark-700 text-white hover:bg-bark-600 shadow-lg shadow-bark-700/25'
                        : 'bg-sand-300/30 text-bark-700 hover:bg-sand-300/50'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 lg:py-28 bg-sand-300/30" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-bark-700 mb-4">
              {t.pricing.compare}
            </h2>
            <p className="text-xl text-bark-700/60">
              {t.pricing.compareDesc}
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="bg-sand-50 rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-sand-300/30">
                      <th className="py-4 px-6 text-start font-semibold text-bark-700">Feature</th>
                      <th className="py-4 px-6 text-center font-semibold text-bark-700">{t.pricing.starter.name}</th>
                      <th className="py-4 px-6 text-center font-semibold text-bark-700 bg-forest/5">
                        {t.pricing.professional.name}
                      </th>
                      <th className="py-4 px-6 text-center font-semibold text-bark-700">{t.pricing.enterprise.name}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.map((row, index) => (
                      <tr key={index} className="border-t border-sand-300">
                        <td className="py-4 px-6 text-bark-700">{row.feature}</td>
                        <td className="py-4 px-6 text-center">
                          {typeof row.starter === 'boolean' ? (
                            row.starter ? (
                              <Check className="w-5 h-5 text-forest mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-sand-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-bark-700">{row.starter}</span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-center bg-forest/5">
                          {typeof row.professional === 'boolean' ? (
                            row.professional ? (
                              <Check className="w-5 h-5 text-forest mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-sand-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-bark-700 font-medium">{row.professional}</span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-center">
                          {typeof row.enterprise === 'boolean' ? (
                            row.enterprise ? (
                              <Check className="w-5 h-5 text-forest mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-sand-400 mx-auto" />
                            )
                          ) : (
                            <span className="text-bark-700">{row.enterprise}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28" dir={dir}>
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-bark-700 mb-4">
              {t.pricing.faq}
            </h2>
            <p className="text-xl text-bark-700/60">
              {t.pricing.faqDesc}
            </p>
          </AnimatedSection>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <AnimatedSection key={index} delay={index * 0.05}>
                <div className="bg-sand-50 rounded-xl border border-sand-300 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-start"
                  >
                    <span className="font-semibold text-bark-700">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-bark-700/60 transition-transform flex-shrink-0 ms-4 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="px-6 pb-6 text-bark-700/60">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-sand-300/30" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScaleIn>
            <div className="bg-gradient-to-br from-bark-700 to-bark-600 rounded-3xl p-12 lg:p-16">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                    {t.pricing.stillQuestions}
                  </h2>
                  <p className="text-xl text-white/90">
                    {t.pricing.stillQuestionsDesc}
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 lg:justify-end">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-accent text-bark-700 font-semibold hover:bg-accent/90 transition-all shadow-lg hover:-translate-y-0.5"
                  >
                    <MessageSquare className="w-5 h-5" />
                    {t.cta.talkToSales}
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white/20 text-white font-semibold hover:bg-white/30 transition-all backdrop-blur-sm"
                  >
                    {t.cta.startTrial}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>
    </LandingLayout>
  );
};
